import api from '@/services/api'
import { dataURLtoBlob } from '@/scenes/engine/utils/image-loader'
import { MediaImageRepositoryProcessingDOM } from '@/scenes/engine/objects/media-repository/MediaImageRepositoryProcessingDOM'
import { Size } from '@/shared/sync/mediaRepository/size'
import { runInline, type SerialLane } from '@/utils/serialLane'

export interface BgRemoveApiResult {
  /** Raw (non-inverted) mask returned by the API, scaled back to original dimensions. */
  resizedMask: string
  originalSize: Size
  targetSize: Size
}

export interface BgRemoveMaskRequestResult {
  /** The API's own mask, base64, still at the squashed model-input dimensions. */
  rawMask: string
  originalSize: Size
  targetSize: Size
}

/**
 * Shared pipeline: preprocess → /bgremove API → postprocess.
 * Returns the raw mask (not yet inverted) plus the original/target sizes.
 *
 * Consumers decide what to do with the mask:
 * - `batchEditService.removeBackground` inverts and composites into a cutout.
 * - `RemoveBackgroundHandler.removeBgInternal` stores into MediaRepository and applies to Fabric.
 *
 * `schedule` wraps the two main-thread pixel stages (the pre-flight resize, and
 * scaling the mask back up) — but not the request between them. A single removal
 * runs them inline; a caller running many at once passes a lane, which keeps their
 * pixel work from interleaving into one unresponsive stretch.
 */
export async function callBgRemoveApi(
  originalDataUrl: string,
  imageProcessing: MediaImageRepositoryProcessingDOM,
  abortSignal?: AbortSignal,
  schedule: SerialLane = runInline,
): Promise<BgRemoveApiResult> {
  const { rawMask, originalSize, targetSize } = await requestBgRemoveMask(
    originalDataUrl,
    imageProcessing,
    abortSignal,
    schedule,
  )
  const resizedMask = await schedule(() =>
    imageProcessing.postProcessFromBazaartBg(
      rawMask,
      originalSize.width / targetSize.width,
      originalSize.height / targetSize.height,
    ),
  )

  return { resizedMask, originalSize, targetSize }
}

/**
 * The request half of the pipeline above, stopping short of unsquashing the mask.
 *
 * For a caller that does its own postprocessing — the batch pass hands the raw
 * mask and the two ratios to a worker, so that resize never runs on the main
 * thread at all.
 */
export async function requestBgRemoveMask(
  originalDataUrl: string,
  imageProcessing: MediaImageRepositoryProcessingDOM,
  abortSignal?: AbortSignal,
  schedule: SerialLane = runInline,
): Promise<BgRemoveMaskRequestResult> {
  const resizedOriginal = await schedule(() => imageProcessing.preprocessForBazaartBg(originalDataUrl))
  const originalSize = await imageProcessing.getImageSize(originalDataUrl)
  const targetSize = await imageProcessing.getImageSize(resizedOriginal)

  const res = await api.bgRemove(
    dataURLtoBlob(resizedOriginal),
    targetSize.width.toString(),
    targetSize.height.toString(),
    abortSignal,
  )

  const rawMask = res.data.replace('\n', '').replace('\\', '')
  return { rawMask, originalSize, targetSize }
}
